import React, { useState, useEffect } from 'react';
import './ProductViewer.css';
import LayoutVisualization from './LayoutVisualization';
import { 
  isLayoutAvailable, 
  getAvailableLayouts, 
  isDoubleDrumAvailable,
  isArrangementAvailable
} from './productRules';

const ProductViewer = ({ selectedModel, config }) => {
  // Mapping between English and Romanian fraction names
  const fractionNames = {
    residual: 'Rezidual',
    biodegradable: 'Biodegradabil',
    plastic: 'Plastic | Metal',
    paper: 'Hârtie | Carton',
    glass: 'Sticlă'
  };

  // Colors for each fraction
  const fractionColors = {
    residual: '#444444',  // dark grey
    glass: '#4CAF50',     // green
    plastic: '#FFC107',   // yellow
    paper: '#2196F3',     // blue
    biodegradable: '#795548' // brown
  };

  // Generate a list with all selected fractions
  const selectedFractions = Object.entries(config.fractions)
    .filter(([_, fractionConfig]) => fractionConfig.enabled)
    .map(([name, fractionConfig]) => ({
      name,
      drumType: fractionConfig.drumType,
      // Add volume information if it exists (will only exist for glass and paper in tip2)
      volume: fractionConfig.volume || '3000'
    }));

  // Reset image error when model or configuration changes
  useEffect(() => {
    // Logic for error reset
  }, [selectedModel, config.layout, config.fractions, config.powerType, config.electricOption]);

  // Generate CSS class for visualization
  const getViewerClasses = () => {
    const classes = ['product-viewer'];
    
    // Add classes based on model
    classes.push(`model-${selectedModel}`);
    
    // Add classes based on power type
    classes.push(`power-${config.powerType}`);
    
    // Add additional classes for electric options if applicable
    if (config.powerType === 'electric' && config.electricOption) {
      classes.push(`electric-${config.electricOption}`);
    }
    
    return classes.join(' ');
  };

  /// Function to build the path to the image for a specific fraction
const getFractionImageSrc = (fraction) => {
  // Get the power option part of the path
  let powerPath = '';
  if (config.powerType === 'electric') {
    // Both independent and grid-connected use the same images
    powerPath = '-electric';
  } else {
    powerPath = '-mechanical';
  }
  
  // For tip2, include volume information in the image path
  let volumePath = '';
  if (selectedModel === 'tip2') {
    if (fraction.name === 'plastic' || fraction.name === 'paper') {
      // Use the selected volume for plastic and paper
      volumePath = `-${fraction.volume || '3000'}L`;
    } else {
      // Fixed 3000L for residual, biodegradable, glass
      volumePath = '-3000L';
    }
  }
  
  // Build the image path
  return `/images/${selectedModel}-${fraction.name}-${fraction.drumType}${volumePath}${powerPath}.png`;
};

  // Function to get wrapper classes for the fraction images
  const getImageWrapperClasses = (fraction) => {
    // Default class
    const classes = ['fraction-image-wrapper'];
    
    // Add large-volume class for 5000L fractions
    if (selectedModel === 'tip2' && 
        (fraction.name === 'plastic' || fraction.name === 'paper') && 
        fraction.volume === '5000') {
      classes.push('large-volume');
    }
    
    return classes.join(' ');
  };

  // Check if the product should show layout-related UI
  const shouldShowLayout = getAvailableLayouts(selectedModel).length > 0;

  // Function to validate whether the double drum is allowed for this fraction
  const isDoubleDrumValid = (fraction) => {
    return isDoubleDrumAvailable(selectedModel, fraction);
  };

// Function to get the label that includes volume for fractions in tip2
const getFractionLabel = (fraction) => {
  let label = fractionNames[fraction.name] || fraction.name;
  
  // Add drum type information
  const drumTypeLabel = fraction.drumType === 'single' ? 'Tambur simplu' : 'Tambur dublu';
  
  // Add volume information for tip2
  if (selectedModel === 'tip2') {
    if (fraction.name === 'plastic' || fraction.name === 'paper') {
      return (
        <>
          {label}
          <span className="drum-type-label">
            {` (${drumTypeLabel}, ${fraction.volume || '3000'}L)`}
          </span>
        </>
      );
    } else {
      // Fixed 3000L for residual, biodegradable, glass
      return (
        <>
          {label}
          <span className="drum-type-label">
            {` (${drumTypeLabel}, 3000L)`}
          </span>
        </>
      );
    }
  }
    
    // Default label with just drum type for non-tip2 models
    return (
      <>
        {label}
        <span className="drum-type-label">
          {` (${drumTypeLabel})`}
        </span>
      </>
    );
  };

  return (
    <div className="viewer-container">
      <h2>Vizualizare produs</h2>
      
      {/* Main visualization */}
      <div className={getViewerClasses()}>
        {selectedFractions.length > 0 ? (
          <div className="fraction-images-container">
            {selectedFractions.map((fraction) => {
              // Warn if double drum is selected but not valid for this fraction
              const isInvalidDoubleDrum = 
                fraction.drumType === 'double' && !isDoubleDrumValid(fraction.name);
              
              return (
                <div 
                  key={fraction.name} 
                  className={getImageWrapperClasses(fraction)}
                >
                  {isInvalidDoubleDrum && (
                    <div className="invalid-config-warning">
                      Tambur dublu nu este disponibil pentru această fracție
                    </div>
                  )}
                  <img
                    src={getFractionImageSrc(fraction)}
                    alt={`${fractionNames[fraction.name] || fraction.name} (${fraction.drumType === 'single' ? 'Tambur simplu' : 'Tambur dublu'}${selectedModel === 'tip2' && (fraction.name === 'plastic' || fraction.name === 'paper') ? `, ${fraction.volume}L` : ''})`}
                    className="fraction-preview-image"
                    onError={(e) => {
                      // Use a placeholder for images that can't be found
                      e.target.onerror = null;
                      e.target.src = `/images/placeholder-${fraction.name}.png`;
                    }}
                  />
                  <div className="fraction-label">
                    {getFractionLabel(fraction)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="preview-placeholder">
            <p className="preview-text">
              Nu ați selectat nicio fracție. Vă rugăm să selectați cel puțin o fracție.
            </p>
          </div>
        )}
      </div>

      {/* Only render LayoutVisualization for products that support layouts */}
      {shouldShowLayout && (
        <LayoutVisualization 
          layout={config.layout}
          selectedFractions={selectedFractions}
          fractionNames={fractionNames}
        />
      )}
    </div>
  );
};

export default ProductViewer;